import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { geographyText, useLocale } from "@ncp/i18n";
import type { CaseDetail, EcFormView } from "./api";

/**
 * The case's own names – its markets and its parties – in the reading language, for panels whose data carries only the English name.
 * Reads the same queries the workspace already holds, so nothing is fetched twice. A name with no Arabic stays as filed.
 */
export function useCaseNames(caseId: string, caseType?: string) {
  const api = useApi();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const detail = useQuery({ queryKey: ["case", caseId], queryFn: () => api.get<CaseDetail>(`/api/cases/${caseId}`), enabled: ar });
  const form = useQuery({ queryKey: ["ec-form", caseId], queryFn: () => api.get<EcFormView>(`/api/ec/${caseId}`), enabled: ar && (caseType ?? detail.data?.summary.type) === "EconomicConcentration", retry: false });

  const markets = useMemo(() => new Map((form.data?.markets ?? []).filter((m) => m.productMarketAr).map((m) => [m.productMarket, m.productMarketAr!])), [form.data]);
  const parties = useMemo(() => new Map((detail.data?.parties ?? []).filter((p) => p.nameAr).map((p) => [p.name, p.nameAr!])), [detail.data]);

  const market = useCallback((name: string | null | undefined) => (!name ? "" : ar ? markets.get(name) ?? name : name), [ar, markets]);
  const party = useCallback((name: string | null | undefined) => (!name ? "" : ar ? parties.get(name) ?? name : name), [ar, parties]);
  const place = useCallback((name: string | null | undefined) => (!name ? "" : geographyText(name, locale)), [locale]);
  return { market, party, place };
}
